import { Injectable } from '@angular/core';
import { EvaluationRanking, PortfolioEvaluation, RankWithStyle } from "../dto/PortfolioEvaluation";
import { EvaluationTotalForm, PortfolioEvaluationForm, PPEvaluations } from "../dto/EvaluationAuxiliary";
import { FormControl, FormGroup } from "@angular/forms";

@Injectable({
  providedIn: 'root',
})
export class EvaluationHelperService {

  /**
   * Calculates the total weight and prepares text for total score
   * @param evaluations - Form, contains an array of portfolio evaluations
   * @returns The total weight and score with decision text.
   */
  static countTotalWeightAndScore(evaluations: PortfolioEvaluationForm[]): EvaluationTotalForm {

    const totalWeight = evaluations
      .map((item) => item.controls.weight.value)
      .reduce((sum, weight) => sum + (Number(weight)), 0);
    const totalScore = evaluations
      .map((item) => item.controls.score.value)
      .reduce((sum, score) => sum + score, 0);

    return new FormGroup({
      weight: new FormControl<number>(totalWeight),
      score: new FormControl<string>(this.prepareDecisionText(totalScore)),
    });
  }

  static prepareRankingList(): RankWithStyle[] {
    const rankings: RankWithStyle[] = [];
    AllRankings.forEach((rank) => rankings.push(this.setStyleToRank(rank)));
    return rankings;
  }

  static getEvaluationFormGroup(portfolioEvaluations: PPEvaluations, uuid: string): PortfolioEvaluationForm {
    const formArray: PortfolioEvaluationForm[] = portfolioEvaluations.controls.ppEvaluations.controls;
    return formArray.find((control) => control.controls.uuid.value === uuid);
  }

  static convertFormsGroupToDTO(portfolioEvaluations: PortfolioEvaluationForm[]): PortfolioEvaluation[] {
    const preparedEvaluations: PortfolioEvaluation[] = [];
    portfolioEvaluations.forEach((pp) => {
      preparedEvaluations.push(this.convertFormToDTO(pp));
    });
    return preparedEvaluations;
  }

  static convertFormToDTO(pp: PortfolioEvaluationForm): PortfolioEvaluation {
    return {
      uuid: pp.controls.uuid.value,
      name: pp.controls.name.value,
      priority: pp.controls.priority.value,
      ranking: pp.controls.ranking.value.rank,
      weight: pp.controls.weight.value / 100,
      comment: pp.controls.comment.value,
      period: pp.controls.period.value,
      companyUUID: pp.controls.companyUUID.value,
      companyName: pp.controls.companyName.value,
    };
  }

  /**
   * Generates a decision score text based on the total score by mapping it to the corresponding index
   * in the {TotalScoreDecision} array.
   *
   * @param totalScore The total score used to determine the decision text.
   * @returns A formatted string containing the total score and the associated decision text.
   */
  static prepareDecisionText(totalScore: number): string {
    // rounds the provided total score to the nearest integer and adjusts it to ensure the index
    // remains within the valid range of the {TotalScoreDecision} array
    const decisionNumber = Math.round(totalScore) - 1;
    let decisionIndex: number;
    if (decisionNumber < 1) {
      decisionIndex = 0;
    } else if (decisionNumber >= TotalScoreDecision.length) {
      decisionIndex = TotalScoreDecision.length;
    } else {
      decisionIndex = decisionNumber;
    }

    return `${totalScore.toFixed(2)} (${TotalScoreDecision[decisionIndex]})`;
  }

  static setStyleToRank(rank: EvaluationRanking): RankWithStyle {
    switch (rank) {
      case EvaluationRanking.SIGNIFICANTLY_WORSE:
        return {
          rank,
          iconNameAndStylingLeft: 'circle.svg; red-10',
          rankingNumber: 1,
        };
      case EvaluationRanking.WORSE:
        return {
          rank,
          iconNameAndStylingLeft: 'circle.svg; orange-9',
          rankingNumber: 2,
        };
      case EvaluationRanking.NO_CHANGE:
        return {
          rank,
          iconNameAndStylingLeft: 'circle.svg; black-7',
          rankingNumber: 3,
        };
      case EvaluationRanking.BETTER:
        return {
          rank,
          iconNameAndStylingLeft: 'circle.svg; blue-3',
          rankingNumber: 4,
        };
      case EvaluationRanking.SIGNIFICANTLY_BETTER:
        return {
          rank,
          iconNameAndStylingLeft: 'circle.svg; green-5',
          rankingNumber: 5,
        };
    }
  }
}

export const AllRankings = [
  EvaluationRanking.SIGNIFICANTLY_WORSE,
  EvaluationRanking.WORSE,
  EvaluationRanking.NO_CHANGE,
  EvaluationRanking.BETTER,
  EvaluationRanking.SIGNIFICANTLY_BETTER,
];

export const TotalScoreDecision = ['Significantly worse', 'Worse', 'No change', 'Better', 'Significantly better'];
